import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Grid,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/client';
import { toast } from 'react-toastify';

const PERMISSION_MODULES = [
  { key: 'users', label: 'User Management' },
  { key: 'properties', label: 'Property Management' },
  { key: 'tenants', label: 'Tenant Management' },
  { key: 'landlords', label: 'Landlord Management' },
  { key: 'units', label: 'Unit Management' },
  { key: 'invoices', label: 'Invoice Management' },
  { key: 'payments', label: 'Payment Management' },
  { key: 'expenses', label: 'Expense Management' },
  { key: 'repairs', label: 'Repair Management' },
  { key: 'notices', label: 'Notice Management' },
  { key: 'reports', label: 'Agency Reports' },
  { key: 'landlordReports', label: 'Landlord Reports' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'settings', label: 'Settings' },
  { key: 'vendors', label: 'Vendor Management' },
  { key: 'expenseCategories', label: 'Expense Categories' },
  { key: 'repairCategories', label: 'Repair Categories' },
  { key: 'requisitions', label: 'Requisitions' },
  { key: 'paymentModes', label: 'Payment Modes' },
  { key: 'noticeCategories', label: 'Notice Categories' },
  { key: 'landlordPayments', label: 'Landlord Payments' },
  { key: 'fixedAssets', label: 'Fixed Assets' },
  { key: 'otherIncome', label: 'Other Income' },
  { key: 'incomeCategories', label: 'Income Categories' },
  { key: 'contraEntries', label: 'Account Transfers' },
  { key: 'tenantExits', label: 'Tenant Exits' },
  { key: 'tenantTransfers', label: 'Tenant Transfers' },
  { key: 'rentIncreases', label: 'Rent Increases' }
];

const PERMISSION_ACTIONS = {
  users: ['view', 'create', 'update', 'delete'],
  properties: ['view', 'create', 'update', 'delete'],
  tenants: ['view', 'create', 'update', 'delete'],
  landlords: ['view', 'create', 'update', 'delete'],
  units: ['view', 'create', 'update', 'delete'],
  invoices: ['view', 'create', 'update', 'delete'],
  // settle = assessing a security deposit at move-out: it pays money back to a tenant and
  // raises a charge against them, so it is granted separately from update.
  payments: ['view', 'create', 'update', 'delete', 'settle'],
  expenses: ['view', 'create', 'update', 'delete', 'approve'],
  // review = stage 2 sign-off, approve = stage 3 final approval
  repairs: ['view', 'create', 'update', 'delete', 'review', 'approve'],
  notices: ['view', 'create', 'update', 'delete'],
  reports: ['view', 'export'],
  landlordReports: ['view', 'export'],
  accounting: ['view', 'create', 'update', 'delete'],
  settings: ['view', 'update'],
  vendors: ['view', 'create', 'update', 'delete'],
  expenseCategories: ['view', 'create', 'update', 'delete'],
  repairCategories: ['view', 'create', 'update', 'delete'],
  // issue = the cashier paying out, deliberately separate from approve
  requisitions: ['view', 'create', 'update', 'delete', 'review', 'approve', 'issue'],
  paymentModes: ['view', 'create', 'update', 'delete'],
  noticeCategories: ['view', 'create', 'update', 'delete'],
  // delete governs voiding — a posted payment is reversed, never removed
  landlordPayments: ['view', 'create', 'update', 'delete'],
  // cancel = reversing a posted receipt, which is not the same as editing one
  otherIncome: ['view', 'create', 'update', 'delete', 'cancel'],
  incomeCategories: ['view', 'create', 'update', 'delete'],
  // reverse = unwinding a posted transfer. Separate from create, because it restates a
  // movement that has been reported on and may already have been reconciled.
  contraEntries: ['view', 'create', 'reverse'],
  fixedAssets: ['view', 'create', 'update', 'delete'],
  tenantExits: ['view', 'create', 'update', 'release'],
  tenantTransfers: ['view', 'create'],
  rentIncreases: ['view', 'create', 'update']
};


const RolePermissions = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [permissions, setPermissions] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    displayName: '',
    description: ''
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/roles?isActive=true');
      if (response.data.length === 0) {
        // Auto-seed system roles if none exist
        try {
          await axios.post('/roles/seed');
          toast.info('System roles initialized');
          // Fetch again after seeding
          const newResponse = await axios.get('/roles?isActive=true');
          setRoles(newResponse.data);
        } catch (seedError) {
          console.error('Error seeding roles:', seedError);
          toast.error('Error initializing system roles');
        }
      } else {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Error loading roles');
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/role-permissions');
      const perms = {};
      response.data.forEach(p => {
        // Ensure all permission modules exist, initialize missing ones with defaults
        const initializedPerms = { ...p };
        if (!initializedPerms.permissions) {
          initializedPerms.permissions = {};
        }
        
        PERMISSION_MODULES.forEach(module => {
          if (!initializedPerms.permissions[module.key]) {
            const actions = PERMISSION_ACTIONS[module.key] || [];
            initializedPerms.permissions[module.key] = {};
            actions.forEach(action => {
              initializedPerms.permissions[module.key][action] = false;
            });
          }
        });
        
        perms[p.role] = initializedPerms;
      });
      setPermissions(perms);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Error loading permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!roleFormData.name || !roleFormData.displayName) {
      toast.error('Role name and display name are required');
      return;
    }

    try {
      await axios.post('/roles', roleFormData);
      toast.success('Role created successfully');
      setRoleDialogOpen(false);
      setRoleFormData({ name: '', displayName: '', description: '' });
      fetchRoles();
      // Create default permissions for new role
      try {
        await axios.put(`/role-permissions/${roleFormData.name.toLowerCase()}`, {
          permissions: getDefaultPermissions()
        });
      } catch (permError) {
        console.error('Error creating default permissions:', permError);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating role');
    }
  };

  const getDefaultPermissions = () => {
    // Default permissions for new custom roles (all false)
    const defaultPerms = {};
    PERMISSION_MODULES.forEach(module => {
      const actions = PERMISSION_ACTIONS[module.key] || [];
      defaultPerms[module.key] = {};
      actions.forEach(action => {
        defaultPerms[module.key][action] = false;
      });
    });
    return defaultPerms;
  };

  const handlePermissionChange = (roleName, module, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [roleName]: {
        ...prev[roleName],
        permissions: {
          ...prev[roleName].permissions,
          [module]: {
            ...prev[roleName].permissions[module],
            [action]: value
          }
        }
      }
    }));
  };

  const handleSave = async () => {
    if (roles.length === 0) {
      toast.error('No roles available');
      return;
    }
    const role = roles[activeTab];
    if (!role) {
      toast.error('No role selected');
      return;
    }
    const roleName = role.name;
    if (!permissions[roleName]) {
      toast.error('No permissions loaded for this role');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`/role-permissions/${roleName}`, {
        permissions: permissions[roleName].permissions
      });
      toast.success(`${role.displayName} permissions saved successfully`);
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will reset all role permissions to default values. Continue?')) {
      return;
    }

    setSaving(true);
    try {
      await axios.post('/role-permissions/seed');
      toast.success('Default permissions seeded successfully');
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error seeding permissions');
    } finally {
      setSaving(false);
    }
  };

  // Superset of every action any module uses, in a fixed, sensible column order.
  // Every action any module uses. A module shows a dash in the columns it does not use.
  const ALL_ACTIONS = ['view', 'create', 'update', 'delete', 'review', 'approve', 'issue', 'export', 'settle', 'cancel', 'reverse', 'release'];

  const renderPermissionMatrix = (role) => {
    const rolePerms = permissions[role];
    if (!rolePerms) return null;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>Module</TableCell>
              {ALL_ACTIONS.map(action => (
                <TableCell key={action} align="center" sx={{ textTransform: 'capitalize' }}>
                  {action}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {PERMISSION_MODULES.map(module => {
              const modulePerms = rolePerms.permissions[module.key] || {};
              const moduleActions = PERMISSION_ACTIONS[module.key] || [];
              return (
                <TableRow key={module.key} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{module.label}</TableCell>
                  {ALL_ACTIONS.map(action => {
                    if (!moduleActions.includes(action)) {
                      return (
                        <TableCell key={action} align="center" sx={{ color: 'text.disabled' }}>
                          —
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={action} align="center">
                        <Checkbox
                          size="small"
                          checked={modulePerms[action] || false}
                          onChange={(e) =>
                            handlePermissionChange(role, module.key, action, e.target.checked)
                          }
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Role Permissions Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setRoleDialogOpen(true)}
          >
            Create Role
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleSeedDefaults}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || roles.length === 0}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure permissions for each role group. Changes take effect immediately after saving.
      </Alert>

      {roles.length > 0 ? (
        <>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            {roles.map((role) => (
              <Tab
                key={role._id}
                label={role.displayName}
                sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
              />
            ))}
          </Tabs>

          {roles[activeTab] && permissions[roles[activeTab].name] ? (
            <Box>
              <Typography variant="h5" sx={{ mb: roles[activeTab].description ? 0.5 : 3 }}>
                {roles[activeTab].displayName} Permissions
              </Typography>
              {roles[activeTab].description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {roles[activeTab].description}
                </Typography>
              )}
              {renderPermissionMatrix(roles[activeTab].name)}
            </Box>
          ) : (
            <Alert severity="warning">
              No permissions configured for {roles[activeTab]?.displayName || 'this role'}. 
              Click "Reset to Defaults" to initialize.
            </Alert>
          )}
        </>
      ) : (
        <Alert 
          severity="info" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={async () => {
                try {
                  await axios.post('/roles/seed');
                  toast.success('System roles initialized successfully');
                  fetchRoles();
                  // Also seed permissions
                  try {
                    await axios.post('/role-permissions/seed');
                    fetchPermissions();
                  } catch (permError) {
                    console.error('Error seeding permissions:', permError);
                  }
                } catch (error) {
                  toast.error(error.response?.data?.message || 'Error initializing roles');
                }
              }}
            >
              Initialize System Roles
            </Button>
          }
        >
          No roles found. Click "Initialize System Roles" to create the default roles (Admin, Staff, Tenant), or "Create Role" to add a custom role.
        </Alert>
      )}

      {/* Create Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Role Name"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                helperText="Lowercase, no spaces (e.g., 'manager', 'accountant')"
                placeholder="manager"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Display Name"
                value={roleFormData.displayName}
                onChange={(e) => setRoleFormData({ ...roleFormData, displayName: e.target.value })}
                helperText="How this role will be displayed (e.g., 'Property Manager')"
                placeholder="Property Manager"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                helperText="Optional description of this role's purpose"
                placeholder="Manages properties and tenants"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRoleDialogOpen(false);
            setRoleFormData({ name: '', displayName: '', description: '' });
          }}>
            Cancel
          </Button>
          <Button onClick={handleCreateRole} variant="contained">
            Create Role
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RolePermissions;

